import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';
import { GridColumnFieldIssue } from 'components/common/dataGrid/columnsFactory/types';
import { isActiveFilterItem } from 'components/common/dataGrid/filters/activity';
import { readFilterRowOperator } from 'components/common/dataGrid/filters/negatedOperators';
import { GridFilterValue } from 'components/common/dataGrid/filters/operators';
import { normalizeIssueNumberForSearch } from 'shared/domain/issue/issueNumber';
import { FIELD_TO_FILTER_CONFIG } from 'shared/domain/issue/mapping/toOut/issueFieldMappingConfig';
import {
  ISSUE_STRING_OPERATOR_VALUES,
  IssueMappedFilterInput,
  issueFiltersToFilterMapped,
} from 'shared/domain/issue/mapping/toOut/issueFiltersToFilterMapped';
import { type FilterCondition, type FilterGroup, type FilterNode, groupOf } from 'shared/filter/filterTree';
import {
  FILTER_LOGICAL_OPERATOR,
  FilterLogicalOperator,
} from 'shared/filter/types';
import { toSingleFilters } from 'shared/utils/filters';
import { isDefinedValue } from 'shared/utils/value';
import { type IssueFilterModel, type IssueFilterPanelNode, listFilterPanelNodes } from './filterGroups';

export type IssueFilterLogicalOperator = FilterLogicalOperator;

type IssueStringOperatorValue = Extract<
  IssueMappedFilterInput,
  { kind: 'string' }
>['type'];

const ISSUE_STRING_OPERATOR_SET: ReadonlySet<string> = new Set(
  ISSUE_STRING_OPERATOR_VALUES
);

type DateRangeFilterValue =
  | { start?: number | null; end?: number | null }
  | undefined;

function isGridStringOperator(
  operator: unknown
): operator is IssueStringOperatorValue {
  return typeof operator === 'string' && ISSUE_STRING_OPERATOR_SET.has(operator);
}

function toStringArray(value: unknown): string[] {
  const valueArray = Array.isArray(value) ? value : [value];

  return valueArray.filter(
    (valueItem): valueItem is string =>
      typeof valueItem === 'string' && valueItem.length > 0
  );
}

function normalizeIssueNumberSegment(value: string): string {
  return normalizeIssueNumberForSearch(value);
}

function normalizeIssueNumberFilterValue(item: GridFilterItem): unknown {
  return normalizeCodeSearchGridFilterValue(item);
}

function normalizeCodeSearchGridFilterValue(item: GridFilterItem): unknown {
  const isNormalizedField =
    item.field === GridColumnFieldIssue.issueNumber ||
    item.field === GridColumnFieldIssue.inspectionNumber;

  if (!isNormalizedField) {
    return item.value;
  }

  if (typeof item.value === 'string') {
    return normalizeIssueNumberSegment(item.value);
  }

  if (Array.isArray(item.value)) {
    return item.value
      .map((value) =>
        typeof value === 'string'
          ? normalizeIssueNumberSegment(value)
          : value
      )
      .filter(Boolean);
  }

  return item.value;
}

function toDateRangeFilterInput(
  path: string,
  value: unknown
): IssueMappedFilterInput {
  const range = value as DateRangeFilterValue;

  return {
    kind: 'dateRange',
    path,
    start: range?.start,
    end: range?.end,
  };
}

function toStringFilterInput(
  path: string,
  item: GridFilterItem
): IssueMappedFilterInput | null {
  const normalizedValue = normalizeIssueNumberFilterValue(item);

  if (!isGridStringOperator(item.operator)) {
    return null;
  }

  if (item.operator === 'isAnyOf') {
    const values = toStringArray(normalizedValue);

    if (!values.length) {
      return null;
    }

    return {
      kind: 'string',
      path,
      type: item.operator,
      value: values,
    };
  }

  if (
    item.operator === 'isEmpty' ||
    item.operator === 'isNotEmpty'
  ) {
    return {
      kind: 'string',
      path,
      type: item.operator,
    };
  }

  if (
    typeof normalizedValue !== 'string' ||
    normalizedValue.length === 0
  ) {
    return null;
  }

  return {
    kind: 'string',
    path,
    type: item.operator,
    value: normalizedValue,
  };
}

function toIdentifiersFilterInput(
  path: string,
  type: 'direct' | 'oneOfDirect',
  value: unknown
): IssueMappedFilterInput | null {
  const values = toStringArray(value);

  if (!values.length) {
    return null;
  }

  return {
    kind: 'identifiers',
    path,
    type,
    values,
  };
}

function toIssueMappedFilterInput(
  item: GridFilterItem
): IssueMappedFilterInput | null {
  const mapping = FIELD_TO_FILTER_CONFIG[item.field];
  if (!mapping) {
    return null;
  }

  if (item.operator === GridFilterValue.IS_EMPTY) {
    return {
      kind: 'string',
      path: mapping.path,
      type: GridFilterValue.IS_EMPTY,
    };
  }
  if (item.operator === GridFilterValue.IS_NOT_EMPTY) {
    return {
      kind: 'string',
      path: mapping.path,
      type: GridFilterValue.IS_NOT_EMPTY,
    };
  }

  if (mapping.type === 'dateRange') {
    return toDateRangeFilterInput(mapping.path, item.value);
  }

  if (mapping.type === 'string') {
    const path =
      item.field === GridColumnFieldIssue.issueNumber
        ? 'issueNumberSearchNormalized'
        : item.field === GridColumnFieldIssue.inspectionNumber
          ? 'inspectionNumberSearchNormalized'
          : mapping.path;
    return toStringFilterInput(path, item);
  }

  return toIdentifiersFilterInput(mapping.path, mapping.type, item.value);
}

/**
 * The row as a condition of the query; nothing for a row without a value or
 * a column the query cannot filter. An "is not" row applies the operator it
 * negates, with the condition negated.
 */
function toFilterCondition(row: GridFilterItem): FilterCondition | undefined {
  if (!isActiveFilterItem(row)) {
    return undefined;
  }
  const { operator, negated } = readFilterRowOperator(row.operator);
  const input = toIssueMappedFilterInput({ ...row, operator });
  const [filter] = input ? toSingleFilters(issueFiltersToFilterMapped([input])) : [];
  if (!filter) {
    return undefined;
  }
  return negated ? { ...filter, negated } : filter;
}

/** How many rows filter the issues. */
export function getUserFiltersCount(
  model: GridFilterModel | undefined
): number {
  return (model?.items ?? []).map(toFilterCondition).filter(isDefinedValue).length;
}

/** The condition of a row, or the group of conditions of a group of rows; nothing when none filters. */
function toFilterNode(node: IssueFilterPanelNode): FilterNode | undefined {
  if (node.kind === 'row') {
    return toFilterCondition(node.row);
  }
  const conditions = node.rows.map(toFilterCondition).filter(isDefinedValue);
  return conditions.length ? groupOf(toFilterLogicalOperator(node.logicOperator), conditions) : undefined;
}

/**
 * The grid's filter rows as the query's group of conditions: the rows outside
 * groups and the groups joined by the grid's logical operator, the rows of a
 * group by the group's own.
 */
export function gridFilterModelToFilterGroup(model: IssueFilterModel | undefined): FilterGroup {
  const nodes = model ? listFilterPanelNodes(model).map(toFilterNode).filter(isDefinedValue) : [];

  return groupOf(
    nodes.length ? gridFilterModelToLogicalOperator(model) : FILTER_LOGICAL_OPERATOR.AND,
    nodes
  );
}

function toFilterLogicalOperator(rawOperator: unknown): IssueFilterLogicalOperator {
  const normalizedOperator =
    typeof rawOperator === 'string' ? rawOperator.toLowerCase() : '';

  return normalizedOperator === FILTER_LOGICAL_OPERATOR.OR
    ? FILTER_LOGICAL_OPERATOR.OR
    : FILTER_LOGICAL_OPERATOR.AND;
}

export function gridFilterModelToLogicalOperator(
  model: GridFilterModel | undefined
): IssueFilterLogicalOperator {
  const getLegacyLinkOperator = (
    filterModel: GridFilterModel | undefined
  ): unknown => (filterModel ? Reflect.get(filterModel, 'linkOperator') : undefined);

  return toFilterLogicalOperator(model?.logicOperator ?? getLegacyLinkOperator(model));
}

function normalizeForComparison(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeForComparison(item))
      .sort((left, right) =>
        JSON.stringify(left).localeCompare(JSON.stringify(right))
      );
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .reduce<Record<string, unknown>>((accumulator, [key, objectValue]) => {
        accumulator[key] = normalizeForComparison(objectValue);
        return accumulator;
      }, {});
  }

  return value;
}

export function getIssueFilterModelUiSignature(
  model: IssueFilterModel
): string {
  return JSON.stringify({
    logicOperator: model.logicOperator,
    groupLogicOperators: model.groupLogicOperators,
    items: (model.items ?? []).map((item) => ({
      id: item.id,
      field: item.field,
      operator: item.operator,
      value: item.value,
      groupId: item.groupId,
    })),
  });
}

export function getIssueFilterModelApiSignature(
  model: IssueFilterModel
): string {
  return JSON.stringify(normalizeForComparison(gridFilterModelToFilterGroup(model)));
}
